import React, { useCallback } from 'react'
import styled from 'styled-components'

import TripEditChip from '../Buttons/TripEditChip'
import UpdateTripDestinations from '../TripModal/Trips/UpdateTripDestinations'
import UpdateTripOrigin from '../TripModal/Trips/UpdateTripOrigin'

import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import LinePlaneDepartureIcon from 'components/Luxkit/Icons/line/LinePlaneDepartureIcon'
import { mediaQueryOnly, mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { last } from 'lib/arrayUtils'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { FullTrip, PlaceGeo } from 'tripPlanner/types/common'

const ChipTextShort = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;

  ${mediaQueryOnly.sm} {
    display: none;
  }

  ${mediaQueryUp.lg} {
    display: none;
  }
`

const ChipTextLong = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  display: none;

  ${mediaQueryOnly.sm} {
    display: block;
  }

  ${mediaQueryUp.lg} {
    display: block;
  }
`

function getPlaceShortLabel(place: PlaceGeo) {
  if (place.type === 'country') {
    return place.lePlaceName
  }
  if (place.shortName && place.countryCode) {
    return `${place.shortName}, ${place.countryCode}`
  }
  const placeNameParts = place.lePlaceName.split(/,\s*/)
  if (placeNameParts.length > 1) {
    return `${placeNameParts[0]}, ${last(placeNameParts)}`
  }
  return place.lePlaceName
}

export function getDestinationsLabel(
  destinations: Array<PlaceGeo>,
  numberOfPlacesToShow: number,
): string {
  let label = destinations
    .slice(0, numberOfPlacesToShow)
    .map(getPlaceShortLabel)
    .join(' • ')
  if (destinations.length > numberOfPlacesToShow) {
    label += ` + ${destinations.length - numberOfPlacesToShow} more`
  }
  return label
}

interface Props {
  trip: FullTrip
}

export default function TripOriginAndDestinations({ trip }: Props) {
  const openModal = useOpenModal()
  const { canEdit } = useTripPermissions(trip.id)

  const shortOriginText = trip.originGeo
    ? getPlaceShortLabel(trip.originGeo)
    : 'Departure location'
  const longOriginText = trip.originGeo
    ? getPlaceShortLabel(trip.originGeo)
    : 'Select departure location'

  const destinationsGeo = trip.destinationsGeo
  const shortDestinationsText =
    destinationsGeo && destinationsGeo.length > 0
      ? getDestinationsLabel(destinationsGeo, 1)
      : 'Destinations'
  const longDestinationsText =
    destinationsGeo && destinationsGeo.length > 0
      ? getDestinationsLabel(destinationsGeo, 2)
      : 'Add destinations'

  const openOriginModal = useCallback(() => {
    openModal(
      UpdateTripOrigin,
      {
        tripId: trip.id,
      },
      {
        desktopHeight: 'max',
      },
    )
  }, [trip.id, openModal])

  const openDestinationsModal = useCallback(() => {
    const initialDestinations = destinationsGeo
      ? destinationsGeo.map((d) => ({
          id: d.lePlaceId,
          name: getPlaceShortLabel(d),
        }))
      : []
    openModal(
      UpdateTripDestinations,
      {
        tripId: trip.id,
        initialDestinations,
      },
      {
        desktopHeight: 'max',
      },
    )
  }, [trip.id, destinationsGeo, openModal])

  return (
    <Group direction="horizontal" verticalAlign="center" gap={8} wrap="wrap">
      <TripEditChip
        startIcon={<LinePlaneDepartureIcon />}
        onClick={openOriginModal}
        canEdit={canEdit}
        showInReadOnly={!!trip.originGeo}
      >
        <ChipTextShort>{shortOriginText}</ChipTextShort>
        <ChipTextLong>{longOriginText}</ChipTextLong>
      </TripEditChip>
      <TripEditChip
        startIcon={<LineMapMarkerIcon />}
        onClick={openDestinationsModal}
        canEdit={canEdit}
        showInReadOnly={!!destinationsGeo && destinationsGeo.length > 0}
      >
        <ChipTextShort>{shortDestinationsText}</ChipTextShort>
        <ChipTextLong>{longDestinationsText}</ChipTextLong>
      </TripEditChip>
    </Group>
  )
}
